import { getFieldValue } from '../../utils/POIFieldExtractor';
import { isStoreyType } from '../../services/spatial/StoreyClassifier';

export type AssetFilterChoices = Record<string, string[]>;
export type AssetFacetReader = (poi: any, field: string) => string[];
export const UNASSIGNED = '\u0000unassigned';
export const ASSET_FACETS = ['building', 'level', 'room_name', 'category', 'system_category', 'source_file'] as const;
const unique = (values: unknown[]): string[] => [...new Set(values.filter(v => v !== null && v !== undefined && String(v).trim()).map(v => String(v).trim()))];

/** Resolve actual assignment paths; never split a room/floor name at commas. */
export function createAssetFacetReader(locations: any[] = []): AssetFacetReader {
  const byId = new Map(locations.map(location => [String(location.id), location]));
  const cache = new WeakMap<object, Map<string, string[]>>();
  return (poi, field) => {
    let fields = cache.get(poi);
    if (!fields) { fields = new Map(); cache.set(poi, fields); }
    if (fields.has(field)) return fields.get(field)!;
    const paths: any[][] = [];
    const assigned = Array.isArray(poi.locations) && poi.locations.length
      ? poi.locations : [{ id: poi.effective_location_id || poi.location_id, name: poi.location_name, type: poi.location_type }];
    assigned.forEach((assignment: any) => {
      const path: any[] = []; const seen = new Set<string>();
      let node = byId.get(String(assignment.id));
      while (node && !seen.has(String(node.id))) {
        seen.add(String(node.id)); path.push(node); node = byId.get(String(node.parent_id));
      }
      if (path.length) paths.push(path);
    });
    const typeOf = (node: any) => String(node.type || node.location_type || '').toLowerCase();
    const isBuilding = (node: any) => ['building', 'buildings'].includes(typeOf(node));
    const pathLabel = (path: any[], at: number) => path.slice(at).filter(n => isBuilding(n) || isStoreyType(n.type || n.location_type) || ['spaces','space','room'].includes(typeOf(n))).map(n => n.name).filter(Boolean).join(' · ');
    let values: unknown[] = [];
    if (field === 'building') {
      values = paths.flatMap(path => path.filter(isBuilding).map(n => n.name));
    } else if (field === 'room_name') {
      values = paths.flatMap(path => path.flatMap((n, i) => ['spaces','space','room'].includes(typeOf(n)) ? [pathLabel(path, i)] : []));
      if (!values.length) values = (poi.locations || []).map((n: any) => [n.name, n.level_name].filter(Boolean).join(' · '));
      if (!values.length && poi.location_type === 'Spaces') values = [poi.location_name];
    } else if (field === 'level') {
      values = paths.flatMap(path => path.flatMap((n, i) => isStoreyType(n.type || n.location_type) ? [pathLabel(path, i)] : []));
      if (!values.length) values = (poi.locations || []).map((n: any) => n.level_name).filter(Boolean);
      if (!values.length) values = [getFieldValue(poi, 'level')];
    } else {
      values = [getFieldValue(poi, field)];
    }
    const result = unique(values); const normalized = result.length ? result : [UNASSIGNED];
    fields.set(field, normalized); return normalized;
  };
}

/** Multiple choices in one question include either; separate questions narrow together. */
export function matchesAssetChoices(poi: any, choices: AssetFilterChoices, read: AssetFacetReader, except?: string): boolean {
  return Object.entries(choices).every(([field, selected]) => field === except || !selected.length || read(poi, field).some(value => selected.includes(value)));
}
export function assetFacetOptions(pois: any[], field: string, choices: AssetFilterChoices, read: AssetFacetReader): { value: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const poi of pois) {
    for (const value of read(poi, field)) {
      if (!counts.has(value)) counts.set(value, 0);
      if (matchesAssetChoices(poi, choices, read, field)) counts.set(value, counts.get(value)! + 1);
    }
  }
  for (const value of choices[field] || []) if (!counts.has(value)) counts.set(value, 0);
  return [...counts].map(([value, count]) => ({ value, count })).sort((a, b) => a.value.localeCompare(b.value, undefined, { numeric: true }));
}
/** Toggle THIS page by identity, retaining deliberate selections on other pages. */
export function toggleAssetPageSelection(selected: Set<string>, ids: string[]): Set<string> {
  const next = new Set(selected); const all = ids.length > 0 && ids.every(id => selected.has(id));
  ids.forEach(id => all ? next.delete(id) : next.add(id)); return next;
}
export function normalizeAssetChoices(value: unknown): AssetFilterChoices {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.fromEntries(Object.entries(value).filter(([, values]) => Array.isArray(values)).map(([key, values]) => [key, (values as unknown[]).filter((v): v is string => typeof v === 'string')]));
}
